/*eslint max-lines-per-function: ["error", 250]*/
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useAuth } from '../../../common/hooks/useAuth';
import { ExamSearch } from '../../components/Exam/ExamSearch';
import { JobConfigInfo } from '../../components/JobConfig/JobConfigInfo';
import { JobTypeEnum } from '../../../common/spdCore/validation/schemas';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { JudgmentsSearch } from '../../components/Judgments/JudgmentsSearch';
import { CardsSearch } from '../../components/Cards/CardsSearch';
import { InfoAccordion } from '../../../common/components/InfoAccordion';

enum ExamsAutomaticJudgmentTabs {
  EXAMS = 'exams',
  JUDGMENTS = 'judgments',
  CARDS = 'cards',
  EXAMS_WITHOUT_PAYMENT = 'examsWithoutPayment',
}

export const ExamsAutomaticJudgmentView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const szafir = useSzafir();
  const auth = useAuth();
  const [refresh, setRefresh] = useState<boolean>(false);

  const onRefresh = () => {
    setRefresh(prev => !prev);
  };

  useEffect(() => {
    if (auth.canSign) {
      szafir.activate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.canSign]);

  const [activeTab, setActiveTab] = React.useState<string>(
    ExamsAutomaticJudgmentTabs.EXAMS,
  );

  const handleClick = async (id: string) => {
    void nav.examTests(id);
  };

  const getExams = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.exams.getForAutomaticJudgmentByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.exams],
  );

  const getJudgments = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.judgments.getAllAutomaticByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        onRefresh,
      ),
    [spd.judgments],
  );

  const getCards = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.cards.getAllAutomaticByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.cards],
  );

  const getExamsWithoutPayment = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.exams.getExamsWithAutoJudgmentWithoutPaymentByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.exams],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          Automatyzacja wystawiania orzeczeń
        </Card.Title>
        <TabsHistory
          activeKey={activeTab}
          setActiveTab={setActiveTab}
          id="automatic-judgment"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={ExamsAutomaticJudgmentTabs.EXAMS}
            title={'Badania oczekujące'}
            className="m-3"
          >
            <ExamSearch
              getData={getExams}
              getCount={spd.exams.getForAutomaticJudgmentCount}
              onRowClick={handleClick}
              initialOrderColumn="exams.endDate"
              initialOrderDirection="ASC"
              withFilterInColumns={true}
            />
            <InfoAccordion
              title="Jakie badania trafiają na tę listę?"
              className="mt-3"
            >
              <p>
                Na liście pojawiają się badania spełniające{' '}
                <strong>wszystkie</strong> poniższe warunki:
              </p>
              <ul>
                <li>
                  Badanie zostało zakończone przez badanego (ma datę
                  zakończenia)
                </li>
                <li>
                  Badanie nie jest oznaczone, że wystawiono orzeczenia poza
                  badaniem
                </li>
                <li>Badanie nie jest zablokowane</li>
                <li>Badanie nie ma żadnych aktywnych orzeczeń</li>
                <li>Badanie ma dokładnie jeden artykuł do wystawienia</li>
                <li>
                  Artykuł jest oznaczony jako gotowy do automatycznego
                  wystawiania orzeczeń
                </li>
                <li>
                  Badanie nie ma punktu krytycznego (np. wyniki wymagające
                  konsultacji specjalisty)
                </li>
                <li>
                  Jeśli badanie ma czynniki szkodliwe — artykuł nie wymaga ich
                  obsługi
                </li>
              </ul>
              <br />
              <p>
                Uwaga - nie zostanie wystawione orzeczenie Uczeń/student,
                jeżeli:
              </p>
              <ul>
                <li>Nie ma w badaniu podanej daty końca nauki</li>
                <li>
                  Nie załączono do badanego skierowania w ciagu ostatnich 2
                  miesięcy
                </li>
                <li>
                  Jedyne skierowanie załączone do badanego to skierowanie
                  zewnętrzne bez podpisu
                </li>
              </ul>
            </InfoAccordion>
            <JobConfigInfo
              items={[
                {
                  jobType: JobTypeEnum.AutomaticJudgment,
                  nextStartLabel:
                    'Następne uruchomienie automatycznego wystawiania orzeczeń',
                  frequencyLabel:
                    'Częstotliwość automatycznego wystawiania orzeczeń',
                },
              ]}
            />
          </Tab>
          <Tab
            eventKey={ExamsAutomaticJudgmentTabs.JUDGMENTS}
            title={'Orzeczenia wystawione automatycznie'}
            className="m-3"
          >
            <JudgmentsSearch
              getData={getJudgments}
              getCount={spd.judgments.getAllAutomaticCount}
              withFilterInColumns={true}
              refresh={refresh}
            />
          </Tab>
          <Tab
            eventKey={ExamsAutomaticJudgmentTabs.CARDS}
            title={'Karty wygenerowane automatycznie'}
            className="m-3"
          >
            <CardsSearch
              getData={getCards}
              getCount={spd.cards.getAllAutomaticCount}
              withFilterInColumns={true}
            />
          </Tab>
          <Tab
            eventKey={ExamsAutomaticJudgmentTabs.EXAMS_WITHOUT_PAYMENT}
            title={'Badania bez rozliczenia'}
            className="m-3"
          >
            <ExamSearch
              getData={getExamsWithoutPayment}
              getCount={spd.exams.getExamsWithAutoJudgmentWithoutPaymentCount}
              onRowClick={handleClick}
              withFilterInColumns={true}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
